import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { createCanvas, get2d, rasteriseContours } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { FluidValues } from './params';

/**
 * Stable-fluids solver (Jos Stam, "Stable Fluids", SIGGRAPH 1999) with the text as an
 * obstacle.
 *
 * The method is unconditionally stable because advection is done by tracing backwards
 * along the velocity field and sampling, rather than pushing quantities forward — no
 * timestep can blow it up. Diffusion and pressure projection are Gauss-Seidel solves.
 *
 * Stam's solver is famously smooth to a fault: numerical dissipation eats the small
 * vortices that make fluid look like fluid. Vorticity confinement adds a force along
 * the curl gradient to put them back, which is why that parameter matters more than the
 * viscosity does.
 */
export interface FluidState {
  n: number;
  width: number;
  height: number;
  size: number;

  u: Float32Array;
  v: Float32Array;
  u0: Float32Array;
  v0: Float32Array;
  density: Float32Array;
  density0: Float32Array;
  curl: Float32Array;
  solid: Uint8Array;

  output: OffscreenCanvas | HTMLCanvasElement;
  octx: Ctx2D;
  image: ImageData | null;
  palette: ResolvedPalette;
  stepsDone: number;
  targetSteps: number;
  dirty: boolean;
}

function values(ctx: TechniqueContext): FluidValues {
  return ctx.values as unknown as FluidValues;
}

const IX = (x: number, y: number, w: number): number => y * w + x;

/** Gauss-Seidel relaxation shared by the diffusion and projection steps. */
function linearSolve(
  target: Float32Array,
  source: Float32Array,
  a: number,
  c: number,
  w: number,
  h: number,
  solid: Uint8Array,
  iterations = 12,
): void {
  const inverseC = 1 / c;
  for (let k = 0; k < iterations; k++) {
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = IX(x, y, w);
        if (solid[i]) {
          target[i] = 0;
          continue;
        }
        target[i] =
          (source[i] +
            a * (target[i - 1] + target[i + 1] + target[i - w] + target[i + w])) *
          inverseC;
      }
    }
  }
}

function advect(
  target: Float32Array,
  source: Float32Array,
  u: Float32Array,
  v: Float32Array,
  dt: number,
  w: number,
  h: number,
  solid: Uint8Array,
): void {
  const dtx = dt * (w - 2);
  const dty = dt * (h - 2);

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = IX(x, y, w);
      if (solid[i]) {
        target[i] = 0;
        continue;
      }
      // Trace backwards and sample — the core of Stam's stability.
      let px = x - dtx * u[i];
      let py = y - dty * v[i];
      if (px < 0.5) px = 0.5;
      if (px > w - 1.5) px = w - 1.5;
      if (py < 0.5) py = 0.5;
      if (py > h - 1.5) py = h - 1.5;

      const x0 = px | 0;
      const y0 = py | 0;
      const sx = px - x0;
      const sy = py - y0;
      const i00 = IX(x0, y0, w);

      target[i] =
        (1 - sx) * ((1 - sy) * source[i00] + sy * source[i00 + w]) +
        sx * ((1 - sy) * source[i00 + 1] + sy * source[i00 + w + 1]);
    }
  }
}

function project(u: Float32Array, v: Float32Array, p: Float32Array, div: Float32Array, w: number, h: number, solid: Uint8Array): void {
  const scale = -0.5 / Math.max(w, h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = IX(x, y, w);
      div[i] = solid[i] ? 0 : scale * (u[i + 1] - u[i - 1] + v[i + w] - v[i - w]);
      p[i] = 0;
    }
  }
  linearSolve(p, div, 1, 4, w, h, solid, 16);

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = IX(x, y, w);
      if (solid[i]) {
        u[i] = 0;
        v[i] = 0;
        continue;
      }
      u[i] -= (0.5 * (p[i + 1] - p[i - 1])) / scale / Math.max(w, h) / 2;
      v[i] -= (0.5 * (p[i + w] - p[i - w])) / scale / Math.max(w, h) / 2;
    }
  }
}

export function init(ctx: TechniqueContext): FluidState {
  const v = values(ctx);
  const width = Math.max(24, Math.min(400, Math.round(v.resolution)));
  const height = Math.max(24, Math.round(width * (ctx.height / Math.max(1, ctx.width))));
  const size = width * height;

  const scaleX = width / ctx.width;
  const scaleY = height / ctx.height;
  const scaled = ctx.field.contours.map((contour) => {
    const points = new Float32Array(contour.points.length);
    for (let i = 0; i < points.length; i += 2) {
      points[i] = contour.points[i] * scaleX;
      points[i + 1] = contour.points[i + 1] * scaleY;
    }
    return { ...contour, points };
  });
  const mask = rasteriseContours(scaled, width, height, 1);

  const solid = new Uint8Array(size);
  if (v.obstacle) for (let i = 0; i < size; i++) if (mask.alpha[i] > 127) solid[i] = 1;

  const state: FluidState = {
    n: width,
    width,
    height,
    size,
    u: new Float32Array(size),
    v: new Float32Array(size),
    u0: new Float32Array(size),
    v0: new Float32Array(size),
    density: new Float32Array(size),
    density0: new Float32Array(size),
    curl: new Float32Array(size),
    solid,
    output: createCanvas(width, height),
    octx: get2d(createCanvas(1, 1)),
    image: null,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    stepsDone: 0,
    targetSteps: v.steps,
    dirty: true,
  };
  state.octx = get2d(state.output);
  state.image = state.octx.createImageData(width, height);
  return state;
}

/** Adds dye and momentum at the configured sources. */
function inject(state: FluidState, v: FluidValues, ctx: TechniqueContext): void {
  const { width: w, height: h, density, u, v: vel } = state;
  const add = (x: number, y: number, fx: number, fy: number): void => {
    const ix = Math.max(1, Math.min(w - 2, x | 0));
    const iy = Math.max(1, Math.min(h - 2, y | 0));
    const i = IX(ix, iy, w);
    if (state.solid[i]) return;
    density[i] += v.injection;
    u[i] += fx;
    vel[i] += fy;
  };

  switch (v.source) {
    case 'bottom':
      for (let x = 1; x < w - 1; x += 2) add(x, h - 3, ctx.rng.signed(v.force * 0.15), -v.force * 0.06);
      break;
    case 'sides':
      for (let y = 1; y < h - 1; y += 2) {
        add(2, y, v.force * 0.06, ctx.rng.signed(v.force * 0.1));
        add(w - 3, y, -v.force * 0.06, ctx.rng.signed(v.force * 0.1));
      }
      break;
    case 'points':
      for (let k = 0; k < 12; k++) {
        add(ctx.rng.next() * w, ctx.rng.next() * h, ctx.rng.signed(v.force * 0.2), ctx.rng.signed(v.force * 0.2));
      }
      break;
    default: {
      // Along the contour, pushing outwards along the normals.
      const samples = ctx.field.sampleOutline(Math.max(32, Math.round(w * 1.5)));
      const scaleX = w / ctx.width;
      const scaleY = h / ctx.height;
      for (const s of samples) {
        add(s.x * scaleX, s.y * scaleY, s.nx * v.force * 0.05, s.ny * v.force * 0.05);
      }
    }
  }
}

/** Vorticity confinement: pushes energy back into the vortices the solver smoothed away. */
function confineVorticity(state: FluidState, strength: number): void {
  const { width: w, height: h, u, v, curl, solid } = state;
  if (strength <= 0) return;

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = IX(x, y, w);
      curl[i] = solid[i] ? 0 : (v[i + 1] - v[i - 1] - (u[i + w] - u[i - w])) * 0.5;
    }
  }

  for (let y = 2; y < h - 2; y++) {
    for (let x = 2; x < w - 2; x++) {
      const i = IX(x, y, w);
      if (solid[i]) continue;
      const dx = Math.abs(curl[i + w]) - Math.abs(curl[i - w]);
      const dy = Math.abs(curl[i - 1]) - Math.abs(curl[i + 1]);
      const len = Math.hypot(dx, dy) + 1e-5;
      const scale = (strength / len) * 0.0002;
      u[i] += dx * scale * curl[i];
      v[i] += dy * scale * curl[i];
    }
  }
}

export function step(state: FluidState, _dt: number, ctx: TechniqueContext): FluidState {
  if (state.stepsDone >= state.targetSteps) return state;
  const v = values(ctx);
  const dt = 0.1;
  const { width: w, height: h } = state;

  const perFrame = Math.max(1, Math.round(2 * clamp01(ctx.quality)));
  for (let f = 0; f < perFrame && state.stepsDone < state.targetSteps; f++) {
    if (state.stepsDone < v.injectUntil) inject(state, v, ctx);

    confineVorticity(state, v.vorticity);

    // Velocity: diffuse, project, advect, project.
    const a = dt * v.viscosity * (w - 2) * (h - 2);
    state.u0.set(state.u);
    state.v0.set(state.v);
    if (a > 0) {
      linearSolve(state.u, state.u0, a, 1 + 4 * a, w, h, state.solid, 8);
      linearSolve(state.v, state.v0, a, 1 + 4 * a, w, h, state.solid, 8);
    }
    project(state.u, state.v, state.u0, state.v0, w, h, state.solid);

    state.u0.set(state.u);
    state.v0.set(state.v);
    advect(state.u, state.u0, state.u0, state.v0, dt, w, h, state.solid);
    advect(state.v, state.v0, state.u0, state.v0, dt, w, h, state.solid);
    project(state.u, state.v, state.u0, state.v0, w, h, state.solid);

    // Density: diffuse then advect.
    const ad = dt * v.diffusion * (w - 2) * (h - 2);
    state.density0.set(state.density);
    if (ad > 0) linearSolve(state.density, state.density0, ad, 1 + 4 * ad, w, h, state.solid, 8);
    state.density0.set(state.density);
    advect(state.density, state.density0, state.u, state.v, dt, w, h, state.solid);

    if (v.decay > 0) {
      const keep = 1 - v.decay;
      for (let i = 0; i < state.size; i++) state.density[i] *= keep;
    }

    state.stepsDone++;
  }

  state.dirty = true;
  return state;
}

export function isComplete(state: FluidState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: FluidState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);

  if (state.dirty && state.image) {
    const data = state.image.data;
    const ramp = state.palette.ramp;
    const rampSteps = ramp.length / 3;

    let max = 0.0001;
    const source = v.showVelocity ? null : state.density;
    if (source) {
      for (let i = 0; i < source.length; i++) if (source[i] > max) max = source[i];
    } else {
      for (let i = 0; i < state.size; i++) {
        const speed = Math.hypot(state.u[i], state.v[i]);
        if (speed > max) max = speed;
      }
    }
    const inverseMax = 1 / max;

    for (let i = 0; i < state.size; i++) {
      const raw = source ? source[i] : Math.hypot(state.u[i], state.v[i]);
      const t = Math.min(1, (raw * inverseMax) ** (1 / v.contrast));
      const r = Math.min(rampSteps - 1, (t * (rampSteps - 1)) | 0) * 3;
      data[i * 4] = ramp[r];
      data[i * 4 + 1] = ramp[r + 1];
      data[i * 4 + 2] = ramp[r + 2];
      data[i * 4 + 3] = Math.round(Math.min(1, t * 1.5) * 255 * v.opacity);
    }

    state.octx.putImageData(state.image, 0, 0);
    state.dirty = false;
  }

  // The simulation grid is much coarser than the canvas; smoothing on the upscale is
  // what makes it read as fluid rather than as a grid of cells.
  const c = target.ctx;
  c.imageSmoothingEnabled = true;
  c.imageSmoothingQuality = 'high';
  c.drawImage(state.output as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(state: FluidState): number {
  return state.size;
}
